/**
 * Slack-like room: message stream (agent finals + user prompts) + composer
 * with state-aware Send / approval-deck. The composer encapsulates the
 * IDLE / RUNNING / AWAITING_APPROVAL state machine; this component only
 * coordinates the upstream services (channel events, approval claim
 * lease, REST history hydration) and feeds the reducer.
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Tab } from '@headlessui/react';
import { List, History, Database, Plug, FolderTree, Loader2, CalendarClock } from 'lucide-react';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { useAgentChannel, appsyncChannelScope } from '@/hooks/useAgentChannel';
import { getRestApiBearerToken, appSyncAgentScopeFromBearerToken } from '@/services/apiToken';
import { fetchNetworkChannelEvents } from '@/services/networkChannelEvents';
import { poolExecute, poolApproval, fetchNetworkSummary } from '@/services/agenticNetworksApi';
import { IncludedTokensExceededError } from '@stepflow/ui-shared/utils/billingErrors';
import { formatCodeConnectorResolutionLine } from '@stepflow/ui-shared/utils/codeConnectorResolution';
import { displayVersionLabel } from '@stepflow/ui-shared/utils/stepflowVersionLabel';
import { economyFromAgents } from '@stepflow/ui-shared/utils/economyMode.js';
import { verboseFromAgents } from '@stepflow/ui-shared/utils/verboseMode.js';
import { manifestToRoster, manifestToReactFlow, isManifest, cappedOutAgentIds } from '@stepflow/ui-shared/utils/manifestToReactFlow.js';
import { mergeRoster } from '@stepflow/ui-shared/utils/agentTree.js';
import RoomBudgetGauge from '@stepflow/ui-shared/components/RoomBudgetGauge.jsx';
import RoomAvatar from '@stepflow/ui-shared/components/dashboard/RoomAvatar.jsx';
import { listNetworkReleases } from '@stepflow/ui-shared/services/networkReleasesApi';
import {
  TRIGGER_HEADLESS,
  TRIGGER_HYBRID,
  normalizeTriggerMode,
} from '@stepflow/ui-shared/utils/networkTrigger';
import { projectRoomLifecycleUiState } from '@stepflow/ui-shared/utils/poolLifecycleUiState.js';
import { WORKBENCH_ACTIVITIES_STREAM_OUTER_PADDING } from '@stepflow/ui-shared/utils/workbenchPromptFieldClassNames.js';
import { useApprovalClaim } from '@/hooks/useApprovalClaim';
import {
  AGENT_EVENTS,
  envelopeEventType,
  UI_STATES,
} from '@/constants/agentEvents';
import { reduceAgentRoomState } from '@/utils/agentRoomReducer';
import { buildAttachmentInputBody } from '@/services/networkAttachmentApi';
import ContextPreviewPanel from './ContextPreviewPanel';
import PoolSettingsDrawer from './PoolSettingsDrawer';
import DelegateMemberDialog from './DelegateMemberDialog';
import Composer from './composer/Composer.jsx';
import AgentRowBuilderAction from './turn/AgentRowBuilderAction.jsx';
import RunBlock from './turn/RunBlock.jsx';
import RouteErrorBoundary from '@stepflow/ui-shell/RouteErrorBoundary.jsx';
import ManifestTreeFlow from '@stepflow/ui-workbench/components/ManifestTreeFlow.jsx';
import useBuilderRoomControls from '../hooks/useBuilderRoomControls.js';
import ExecutionsTab from './executions/ExecutionsTab.jsx';
import SchedulerTab from './scheduler/SchedulerTab.jsx';
import AgenticDataPlaneTab from './AgenticDataPlaneTab.jsx';
import { useNetworkAsl } from '../hooks/useNetworkAsl.js';
import SecretsCatalog from '@stepflow/ui-shared/components/secrets/SecretsCatalog.jsx';
import AgentWorkspaceBrowser from '@stepflow/ui-shared/components/workspace/AgentWorkspaceBrowser.jsx';
import { secretsRegistryV1Enabled } from '@stepflow/ui-shared/services/toolsApi';
import { fetchBuilderCliSlot } from '@stepflow/ui-shared/services/builderRunsApi.js';
import { cancelUniversalBuilderRun } from '@stepflow/ui-shared/services/builderCliApi';
import { getMcpCredentialStatus } from '@stepflow/ui-shared/services/poolMembersApi';
import { canRespondToBuilderApproval, canManageRoomConnectors } from '@stepflow/ui-shared/utils/roomRoles';

function normalizeUiStateFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const u = payload.uiState;
  return u ? String(u).toUpperCase().trim() : null;
}

/** Human-friendly label for the header room-state chip. The wire values (from
 *  `UI_STATES` + the backend, e.g. `AWAITING_INITIAL_INPUT`) are engine enums — never show
 *  them raw. Unknown value → Title-Cased words as a safe fallback (never a raw SCREAMING_CASE). */
const UI_STATE_LABELS = Object.freeze({
  IDLE: 'Idle',
  RUNNING: 'Running',
  AWAITING_INITIAL_INPUT: 'Ready',
  AWAITING_APPROVAL: 'Needs approval',
  COMPLETED: 'Done',
  FAILED: 'Failed',
});
function friendlyUiState(uiState) {
  const k = String(uiState || '').toUpperCase().trim();
  if (!k) return '';
  if (UI_STATE_LABELS[k]) return UI_STATE_LABELS[k];
  return k.toLowerCase().replace(/_+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

// SFN ARN markers for per-agent .sync SUB-executions (worker/builder children) — used to tell a
// top-level run START from a sub-execution when paging channel-events history one run at a time.
const _SUBEXEC_HISTORY_MARKERS = ['universal-worker', 'universal-builder'];

let __nextLocalId = 1;

function freshOptimisticBubble({ text, actorName, correlationId }) {
  __nextLocalId += 1;
  return {
    id: `opt-${__nextLocalId}-${Date.now().toString(36)}`,
    correlationId,
    actorName,
    text,
    ts: new Date().toISOString(),
  };
}

/** Stable hash for `useMemo` dependency on the envelope buffer. We don't
 *  want to re-run the reducer on every reference change of `messages`
 *  while the underlying contents are stable (ie. another render flush in
 *  the same effect tick). */
function envelopeBufferKey(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  const last = arr[arr.length - 1];
  return `${arr.length}:${last?.eventId || last?.timestamp || ''}`;
}

export default function AgenticNetworkRoom({
  networkId,
  poolId,
  displayName,
  /** Caller's legacy single role in this pool — kept for back-compat with
   *  components that haven't moved to ``poolRoles`` yet. */
  poolRole,
  /** Caller's canonical multi-role array in this pool — drives RBAC for
   *  builder approvals and roster management. Includes legacy CREATOR
   *  mapped to OWNER. Empty when the user is not a member of this pool. */
  poolRoles = [],
  /** Release version (from ``GET /my/agentic-networks``). Every room
   *  is pinned to a real ``vN`` at creation time. */
  releaseVersion,
  /** Pool lifecycle status (M5) — drives the composer freeze + persistent
   *  inactivity banner. ``GET /my/agentic-networks`` attaches it server-side
   *  so we don't pay an extra round-trip per room. */
  lifecycleStatus,
  /** ``{ daysUntilRetire }`` warning payload — only present for NOTIFIED
   *  rooms. Server-computed so the banner countdown is consistent across
   *  every member of the pool without TZ drift. */
  inactivityWarning,
  /** Optional deep-link target tab (e.g. ``'executions'`` from the Rooms
   *  landing's "View N executions" CTA). Seeds the initial ``roomTabIndex``;
   *  the user can freely switch afterwards. */
  initialTab,
}) {
  const { user, activeTenantId, workspaceRole } = useAuth();
  const scope = useMemo(() => {
    const fromToken = appSyncAgentScopeFromBearerToken(getRestApiBearerToken());
    return fromToken || appsyncChannelScope(activeTenantId, user?.sub);
  }, [activeTenantId, user?.sub]);

  const { messages, connected } = useAgentChannel(networkId, scope, poolId);

  // Phase 3 — approval claim lease. Multiple supervisors in the same pool
  // subscribe to the same APPROVAL_REQUESTED, so we coordinate ownership
  // via the lease before anyone can actually send APPROVE / DENY / MODIFY
  // to Step Functions.
  const {
    claim: claimApprovalLease,
    delegate: delegateApprovalLease,
    escalate: escalateApprovalLease,
    getApproval: getApprovalLease,
    isSelfClaimer,
  } = useApprovalClaim(networkId, poolId, messages, user?.sub || '');

  // Floating agent-map drag state — persisted per network so each room
  // remembers where the user parked the overlay last time. Resetting via
  // Stream alignment toggle — centered vs full-width. Default is now "center":
  // the stream is capped in the middle so the freed side-space docks the live
  // agent-tree panel (a fuller use of the screen than either edge). "left" (full
  // width) stays available for users who want the stream to span the whole panel.
  // Persisted per user (not per room) since it's a personal preference.
  const _STREAM_ALIGN_KEY = 'stepflow:stream-align';
  const [streamAlign, setStreamAlign] = useState(() => {
    if (typeof window === 'undefined') return 'center';
    try {
      const v = window.localStorage?.getItem(_STREAM_ALIGN_KEY);
      // Honor an explicit stored choice; default (unset) → centered.
      return v === 'left' ? 'left' : 'center';
    } catch (_e) {
      return 'center';
    }
  });
  useEffect(() => {
    try {
      window.localStorage?.setItem(_STREAM_ALIGN_KEY, streamAlign);
    } catch (_e) { /* ignore quota / private mode */ }
  }, [streamAlign]);

  // Docked agent-tree panel: visibility is driven by the stream-alignment toggle (centered = tree
  // shown, full-width = hidden). Width is drag-resizable via the divider; persisted per user.
  const _TREE_W_KEY = 'stepflow:tree-panel-width';
  const _TREE_W_DEFAULT = 560;
  const [treePanelWidth, setTreePanelWidth] = useState(() => {
    if (typeof window === 'undefined') return _TREE_W_DEFAULT;
    const v = Number(window.localStorage?.getItem(_TREE_W_KEY));
    return Number.isFinite(v) && v >= 280 ? Math.min(v, 720) : _TREE_W_DEFAULT;
  });
  useEffect(() => {
    try { window.localStorage?.setItem(_TREE_W_KEY, String(treePanelWidth)); } catch (_e) { /* ignore */ }
  }, [treePanelWidth]);
  // Drag the divider: moving LEFT widens the (right-docked) tree. Bounded 280–720px.
  const _treeResizeStart = useRef(null);
  const onTreeDividerDown = useCallback((e) => {
    _treeResizeStart.current = { x: e.clientX, w: treePanelWidth };
    const onMove = (ev) => {
      const s = _treeResizeStart.current;
      if (!s) return;
      const next = Math.min(720, Math.max(280, s.w + (s.x - ev.clientX)));
      setTreePanelWidth(next);
    };
    const onUp = () => {
      _treeResizeStart.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [treePanelWidth]);

  const [historyEnvelopes, setHistoryEnvelopes] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // Incremental history paging (perf): load only the LAST run on mount, then let the user pull
  // one more at a time. A generative run is ~80-90 fat channel events, and each page is a large
  // (multi-MB) payload that takes ~4s — so eager-loading N runs stacked several 4s serial requests
  // and made the room feel slow to open. ``historyCursor`` = the next-page cursor from the backend
  // (null when the feed is exhausted); ``historyPaging`` gates the "Load previous run" button.
  const [historyCursor, setHistoryCursor] = useState(null);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyPaging, setHistoryPaging] = useState(false);
  const historyRunStartsRef = useRef(new Set());   // distinct top-level run ids seen so far
  /** Optimistic bubbles awaiting backend reconciliation by correlationId. */
  const [optimisticBubbles, setOptimisticBubbles] = useState([]);
  /** TaskIds the supervisor just resolved locally; we suppress the
   *  reducer's pendingApproval until APPROVAL_RESOLVED echoes back. */
  const [resolvedTaskIds, setResolvedTaskIds] = useState([]);
  /** Ephemeral system rows for events that have no AppSync envelope (delegate
   *  success, claim error, etc.). They render below the latest turn. */
  const [localSystemEvents, setLocalSystemEvents] = useState([]);
  const pushLocalSystemEvent = useCallback((text) => {
    setLocalSystemEvents((prev) => [
      ...prev,
      { kind: 'system', id: `sys-${Date.now()}-${prev.length}`, text },
    ]);
  }, []);
  const [sending, setSending] = useState(false);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [contextDrawerOpen, setContextDrawerOpen] = useState(false);
  const [membersDrawerOpen, setMembersDrawerOpen] = useState(false);
  // Room tab control (Activities=0, Context=1, Connections=2, Workspace=3,
  // Executions=4). Controlled so the drawer's "Create in Connections" can switch
  // to the Connections tab. A one-shot ``connectionsAutoOpenTool`` tells the
  // room SecretsCatalog which creator to open. Forward-only (the drawer unmounts
  // on close, so there's no auto-return/assign — the user re-opens the drawer
  // and picks the now-created connection from the list).
  const ROOM_CONNECTIONS_TAB_INDEX = 2;
  const ROOM_EXECUTIONS_TAB_INDEX = 4;
  // Deep-link tab → initial index (only ``executions`` is wired today; extend the
  // map as more tabs get deep-linked). Default Activities (0).
  const ROOM_TAB_INDEX_BY_NAME = { executions: ROOM_EXECUTIONS_TAB_INDEX };
  const [roomTabIndex, setRoomTabIndex] = useState(
    () => ROOM_TAB_INDEX_BY_NAME[String(initialTab || '').toLowerCase()] ?? 0,
  );
  const [connectionsAutoOpenTool, setConnectionsAutoOpenTool] = useState(null);
  const navigateRoomToConnections = useCallback(({ tool } = {}) => {
    setConnectionsAutoOpenTool(tool || null);
    setMembersDrawerOpen(false); // close the drawer so the Connections tab is visible
    setRoomTabIndex(ROOM_CONNECTIONS_TAB_INDEX);
  }, []);
  const [memberCount, setMemberCount] = useState(null);
  const [delegateDialogOpen, setDelegateDialogOpen] = useState(false);
  /** Network designer sub (GET /networks/{id} ``userId``) — gates Edit/Delete on NETWORK-scoped registry rows. */
  const [networkOwnerUserId, setNetworkOwnerUserId] = useState('');
  // Economy/Test mode (frozen __roomContext__ 'Shared' setting for this room's release).
  // DEFAULT ON — matches the backend resolver (unset → on). Drives the "Economy" header chip.
  const [economyMode, setEconomyMode] = useState(true);
  // VERBOSE (cost telemetry in run blocks) — DEFAULT OFF, the mirror of economyMode above.
  // Read from the SAME frozen __roomContext__ fetch so it reflects what the room RUNS with,
  // not a live draft the user hasn't released.
  const [verboseMode, setVerboseMode] = useState(false);
  // Manual stage cap (frozen __roomContext__ Shared setting) — 0 = Auto (no cap). When >0, agents
  // deeper than N stages are DIMMED in the room tree/roster ("part of the team, disabled this
  // release"). Read from the SAME frozen fetch as economyMode below.
  const [stageDepthCap, setStageDepthCap] = useState(0);
  // Releases list — used to render the room's MAJOR.MINOR version label
  // (e.g. "1.10") on the chip next to the room title.
  const [networkReleases, setNetworkReleases] = useState([]);
  useEffect(() => {
    if (!networkId) return undefined;
    let cancelled = false;
    listNetworkReleases(networkId, { limit: 50 })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.releases) ? data.releases : [];
        setNetworkReleases(list);
      })
      .catch(() => {
        if (cancelled) return;
        setNetworkReleases([]);
      });
    return () => { cancelled = true; };
  }, [networkId, releaseVersion]);
  const versionLabel = useMemo(() => {
    const rv = Number(releaseVersion);
    if (!Number.isFinite(rv) || rv < 1) return '';
    return displayVersionLabel(rv, networkReleases);
  }, [networkReleases, releaseVersion]);
  useEffect(() => {
    let cancelled = false;
    if (!networkId || networkId === 'new') return undefined;
    (async () => {
      try {
        // Pass poolId so a release-pinned room gets its FROZEN __roomContext__
        // (the economyMode the room actually runs with, not the live draft).
        const n = await fetchNetworkSummary(networkId, { poolId });
        const uid = String(n?.userId || '').trim();
        if (!cancelled) setNetworkOwnerUserId(uid || '');
        // Economy/Test mode from the (frozen) Shared row — DEFAULT ON: unset/absent → on,
        // off ONLY on an explicit opt-out. Mirrors release_resolution._is_economy_on.
        if (!cancelled) setEconomyMode(economyFromAgents(n?.agents));
        if (!cancelled) setVerboseMode(verboseFromAgents(n?.agents));
        // Manual stage cap from the SAME frozen Shared row (0 = Auto). Drives the dimmed
        // "disabled this release" agents in the tree + roster.
        if (!cancelled) {
          const _rc = (n?.agents && typeof n.agents === 'object') ? n.agents.__roomContext__ : null;
          const _cap = Math.max(0, Number.parseInt(_rc?.stageDepthCap, 10) || 0);
          setStageDepthCap(_cap);
        }
      } catch {
        if (!cancelled) setNetworkOwnerUserId('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [networkId, poolId]);
  /** Stable for ``PoolSettingsDrawer`` — inline lambdas there made ``refresh`` churn every
   *  render and re-trigger ``GET .../members`` in a tight loop. */
  const handleMemberCountFromRoster = useCallback((count) => {
    setMemberCount(count);
  }, []);
  const handleTriggerModeFromDrawer = useCallback((next) => {
    setTriggerMode(normalizeTriggerMode(next));
  }, []);
  /** Pool runtime governance — currently just trigger mode. Defaults to
   *  HYBRID so the prompt-box gating doesn't go pessimistic during the
   *  initial members-fetch (matches pre-Phase-B behavior for legacy pools
   *  on disk that haven't been migrated to a stored TriggerMode yet). */
  const [triggerMode, setTriggerMode] = useState(TRIGGER_HYBRID);
  const bottomRef = useRef(null);

  /** Hydrate from REST history (same shape as live AppSync envelopes). The
   *  reducer treats history + live as one chronological buffer; the live
   *  hook (`useAgentChannel`) already dedupes by eventId on top so a
   *  reconnect won't double-count. */
  // A run START is a top-level USER_PROMPT or non-sub-execution EXECUTION_STARTED (a sub-exec is a
  // per-agent .sync child on universal-worker/-builder — NOT a run boundary). Used to count how
  // many WHOLE runs we've paged in so each loaded run arrives complete (prompt + finals + summary).
  const isRunStartEnvelope = useCallback((env) => {
    const t = envelopeEventType(env);
    if (t === 'USER_PROMPT') return true;
    if (t !== 'EXECUTION_STARTED') return false;
    const arn = String(env?.payload?.stateMachineArn || env?.stateMachineArn || '');
    return !_SUBEXEC_HISTORY_MARKERS.some((m) => arn.includes(m));
  }, []);

  /** Load ONE more complete run into history (newest-first). Pages the channel feed from the
   *  current cursor until it has seen ONE additional top-level run START (so the run loads whole),
   *  or the feed ends. Called once on mount for the latest run, then by the "Load previous run"
   *  button. Small pages (~150) keep each request fast; a hard page backstop prevents a runaway. */
  const loadMoreHistory = useCallback(async () => {
    if (!networkId || !poolId) return;
    setHistoryPaging(true);
    let cursor = historyCursor;
    const startCount = historyRunStartsRef.current.size;
    const collected = [];
    let nextCursor = null;
    let pages = 0;
    const MAX_PAGES = 4;   // backstop: 4 × 150 = 600 events for one run — plenty even for a wide run
    try {
      let more = true;
      while (more) {
        const resp = await fetchNetworkChannelEvents(networkId, { limit: 150, poolId, cursor });
        const page = Array.isArray(resp.events) ? resp.events : [];
        collected.push(...page);
        for (const env of page) {
          if (isRunStartEnvelope(env)) {
            const rid = String(env?.executionId || env?.correlationId || '').trim();
            if (rid) historyRunStartsRef.current.add(rid);
          }
        }
        nextCursor = resp.nextCursor || null;
        cursor = nextCursor;
        pages += 1;
        // Stop once we've pulled in ONE more run's start than we had, or the feed is exhausted.
        const gotOneMoreRun = historyRunStartsRef.current.size > startCount;
        if (!nextCursor || gotOneMoreRun || pages >= MAX_PAGES) more = false;
      }
      // Append older (newest-first) events beneath what we have; reducer re-sorts chronologically.
      // combinedEnvelopes dedupes by eventId, so a boundary overlap can't double-count.
      if (collected.length) {
        setHistoryEnvelopes((prev) => [...prev, ...collected]);
      }
      setHistoryCursor(nextCursor);
      setHistoryHasMore(!!nextCursor);
    } catch {
      /* non-fatal */
    } finally {
      setHistoryPaging(false);
      setHistoryLoaded(true);
    }
  }, [networkId, poolId, historyCursor, isRunStartEnvelope]);

  // On room open / network switch: reset history + load ONLY the latest run (fast). Older runs
  // load on demand via the "Load previous run" button — eager-loading N runs stacked several ~4s
  // requests and made the room slow to open.
  useEffect(() => {
    // DIAG (temporary): make the history load OBSERVABLE. A silent early-return here (missing
    // poolId) is the most likely reason a network switch shows "no API call" in the browser — the
    // effect bails before the fetch. Log the entry + the bail so it can't hide.
    if (!networkId || !poolId) {
      // eslint-disable-next-line no-console
      console.warn('[room-history] SKIPPED history load — no fetch will fire.',
        { networkId, poolId, reason: !networkId ? 'no networkId' : 'no poolId' });
      return undefined;
    }
    // eslint-disable-next-line no-console
    console.info('[room-history] loading last run via GET /channel-events', { networkId, poolId });
    setHistoryEnvelopes([]);
    setHistoryLoaded(false);
    setHistoryCursor(null);
    setHistoryHasMore(false);
    historyRunStartsRef.current = new Set();
    let cancelled = false;
    (async () => {
      const collected = [];
      let cursor;
      let nextCursor = null;
      let pages = 0;
      const MAX_PAGES = 4;
      try {
        let more = true;
        while (more) {
          // eslint-disable-next-line no-console
          console.info('[room-history] fetch page', { networkId, poolId, cursor: cursor || null, pages });
          const resp = await fetchNetworkChannelEvents(networkId, { limit: 150, poolId, cursor });
          if (cancelled) return;
          const page = Array.isArray(resp.events) ? resp.events : [];
          // eslint-disable-next-line no-console
          console.info('[room-history] page result', { events: page.length, nextCursor: resp.nextCursor || null });
          collected.push(...page);
          for (const env of page) {
            if (isRunStartEnvelope(env)) {
              const rid = String(env?.executionId || env?.correlationId || '').trim();
              if (rid) historyRunStartsRef.current.add(rid);
            }
          }
          nextCursor = resp.nextCursor || null;
          cursor = nextCursor;
          pages += 1;
          // Stop as soon as we have ONE whole run (its start seen), or the feed ends.
          if (!nextCursor || historyRunStartsRef.current.size >= 1 || pages >= MAX_PAGES) more = false;
        }
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.info('[room-history] loaded', { totalEvents: collected.length, runsFound: historyRunStartsRef.current.size, hasMore: !!nextCursor });
        setHistoryEnvelopes([...collected].reverse());   // newest-first → chronological
        setHistoryCursor(nextCursor);
        setHistoryHasMore(!!nextCursor);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[room-history] FETCH FAILED — the call fired but errored (auth/404/CORS?):', err);
      } finally {
        if (!cancelled) setHistoryLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [networkId, poolId, isRunStartEnvelope]);

  /** ``memberCount`` + initial ``triggerMode`` from the same ``GET .../members`` that
   *  ``PoolSettingsDrawer`` runs on mount (see there). Avoids duplicate REST calls. */

  /** React to live ``POOL_SETTINGS_CHANGED`` envelopes (creator flips the
   *  trigger mode from the Settings drawer or via Overwatch). The reducer
   *  doesn't need to know about settings — we just sip the latest value
   *  off the envelope buffer ourselves. */
  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) return;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const env = messages[i];
      if (envelopeEventType(env) !== 'POOL_SETTINGS_CHANGED') continue;
      const tm = env?.payload?.settings?.triggerMode;
      if (tm) {
        setTriggerMode(normalizeTriggerMode(tm));
      }
      break;
    }
  }, [messages]);

  /** Combined chronological envelope buffer fed to the reducer. We dedupe
   *  by eventId so a live message that matches a REST-hydrated row wins
   *  the live timestamp and metadata. */
  const messagesKey = envelopeBufferKey(messages);
  const historyKey = envelopeBufferKey(historyEnvelopes);
  /** Prefer live ``BUILDER_INPUT_WAIT`` (non-persistent). After reconnect or
   *  reload, Dynamo-backed hydration merges in below (see ``resolvedBuilderCliWait``).
   *  Auto-dismiss: if a LOG_LINE with stream 'universal_builder' appears AFTER the
   *  last BUILDER_INPUT_WAIT, the builder has resumed and the wait is stale. */
  const activeBuilderCliWait = useMemo(() => {
    if (!Array.isArray(messages) || messages.length === 0) return null;
    // Find the latest WAIT.
    let waitIdx = -1;
    let waitEnv = null;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const env = messages[i];
      if (!env || envelopeEventType(env) !== AGENT_EVENTS.BUILDER_INPUT_WAIT) continue;
      waitIdx = i;
      waitEnv = env;
      break;
    }
    if (!waitEnv) return null;
    const pl = waitEnv.payload && typeof waitEnv.payload === 'object' ? waitEnv.payload : {};
    const eventId = String(waitEnv.eventId || '');
    const runId = String(pl.runId || waitEnv.executionId || waitEnv.correlationId || '').trim();
    if (!eventId || !runId) return null;


    // Match RESOLVED to WAIT by ``waitEventId`` first (precise — covers
    // multi-turn sessions where the same runId hosts many WAITs and
    // out-of-order AppSync delivery). Scan the entire buffer (not only
    // post-WAIT) since a RESOLVED could arrive before its WAIT due to
    // reconnect replay ordering.
    //
    // Fallback: if RESOLVED has no ``waitEventId`` (legacy backend), fall
    // back to the previous heuristic (RESOLVED for same runId appearing
    // *after* the WAIT in chronological order).
    for (let j = 0; j < messages.length; j += 1) {
      const env = messages[j];
      if (!env || envelopeEventType(env) !== AGENT_EVENTS.BUILDER_INPUT_RESOLVED) continue;
      const rPl = env.payload && typeof env.payload === 'object' ? env.payload : {};
      const rWaitId = String(rPl.waitEventId || '').trim();
      if (rWaitId) {
        if (rWaitId === eventId) return null;
        continue;
      }
      // Legacy RESOLVED — only consider if it came after the WAIT.
      if (j <= waitIdx) continue;
      const rRun = String(rPl.runId || env.executionId || env.correlationId || '').trim();
      if (rRun && rRun === runId) return null;
    }
    const promptStr = typeof pl.prompt === 'string' ? pl.prompt : '';
    const interaction = pl.interaction && typeof pl.interaction === 'object' ? pl.interaction : null;
    return { eventId, runId, prompt: promptStr, interaction };
  }, [messages]);

  /** Set of WAIT eventIds that have been answered (any user). Built from
   *  BUILDER_INPUT_RESOLVED envelopes in the live message buffer; matched by
   *  ``waitEventId`` in the payload. Used by the inline "Awaiting approval"
   *  indicator in TurnGroup so peer browsers dismiss it as soon as anyone
   *  responds — same dismiss semantics as the alert band. */
  const resolvedWaitEventIds = useMemo(() => {
    const out = new Set();
    if (!Array.isArray(messages)) return out;
    for (const env of messages) {
      if (!env || envelopeEventType(env) !== AGENT_EVENTS.BUILDER_INPUT_RESOLVED) continue;
      const rPl = env.payload && typeof env.payload === 'object' ? env.payload : {};
      const rWaitId = String(rPl.waitEventId || '').trim();
      if (rWaitId) out.add(rWaitId);
    }
    return out;
  }, [messages]);

  /** When live AppSync misses ``BUILDER_INPUT_WAIT`` after a refresh/reconnect,
   *  hydrate from Dynamo (``stream_runner`` seeds ``inputWaitActive``). */
  const [hydratedBuilderCliWait, setHydratedBuilderCliWait] = useState(null);

  useEffect(() => {
    if (activeBuilderCliWait) {
      setHydratedBuilderCliWait(null);
    }
  }, [activeBuilderCliWait]);

  /** Dismiss WAIT modals locally after send/cancel — same ``eventId`` won't reopen until a new WAIT arrives. */
  const [handledBuilderWaitIds, setHandledBuilderWaitIds] = useState([]);
  const handledBuilderWaitSet = useMemo(() => new Set(handledBuilderWaitIds), [handledBuilderWaitIds]);
  const markBuilderWaitHandled = useCallback((eventId) => {
    if (!eventId) return;
    setHandledBuilderWaitIds((prev) => (prev.includes(eventId) ? prev : [...prev, eventId]));
  }, []);

  const combinedEnvelopes = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (env) => {
      if (!env || typeof env !== 'object') return;
      const id = env.eventId;
      if (id) {
        if (seen.has(id)) return;
        seen.add(id);
      }
      out.push(env);
    };
    historyEnvelopes.forEach(push);
    messages.forEach(push);
    return out;
    // The dedupe sets re-run whenever the buffers' lengths/last id change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyKey, messagesKey]);

  const room = useMemo(
    () => reduceAgentRoomState(combinedEnvelopes, { optimistic: optimisticBubbles }),
    [combinedEnvelopes, optimisticBubbles],
  );
  const {
    turnGroups, uiState, lastExecution,
    agentRoster, pendingBuilderWaits: roomPendingBuilderWaits, currentRunId,
    runReleaseVersions, agentRostersByRun, stageCostByRun,
  } = room;

  // Network topology (the full agent tree) so the roster can be SEEDED stable up-front — fetched +
  // cached; also feeds MiniFlow below. Kept here (not at first render only) so the seeded roster is
  // available before the merge.
  const { asl: networkAsl } = useNetworkAsl(networkId, releaseVersion);

  // STABLE seeded roster: the WHOLE topology, all ``queued``, the instant the manifest is known.
  // The live event-accreted ``agentRoster`` then only MUTATES status onto these rows — the list
  // never jumps/reshuffles as distributed agents report in (predictable layout, the user's ask).
  const seededRoster = useMemo(
    () => (isManifest(networkAsl) ? manifestToRoster(networkAsl) : []),
    [networkAsl],
  );
  // Run is settled → a still-``queued`` seeded row was NOT selected by the Lead (gray it out).
  const runSettled = useMemo(() => {
    const s = String(lastExecution?.status || '').toUpperCase();
    return s === 'COMPLETED' || s === 'FAILED'
      || ['IDLE', 'COMPLETED', 'FAILED'].includes(String(uiState || '').toUpperCase());
  }, [lastExecution?.status, uiState]);
  // The merged roster the panel renders: stable seeded rows overlaid with live status/text/reasoning.
  const mergedRoster = useMemo(
    () => (seededRoster.length
      ? mergeRoster(seededRoster, agentRoster, { runSettled, stageDepthCap })
      : agentRoster),
    [seededRoster, agentRoster, runSettled, stageDepthCap],
  );
  // Status map {agentId → {status, notSelected}} for the docked live tree — colors each node by the
  // AppSync stream in real time (running/awaiting/done/failed/skipped). Derived from the merged
  // roster so it matches the line-item panel exactly.
  const treeStatusByAgentId = useMemo(() => {
    const m = {};
    for (const r of (Array.isArray(mergedRoster) ? mergedRoster : [])) {
      if (r && r.agentId) m[r.agentId] = { status: r.status, notSelected: !!r.notSelected };
    }
    return m;
  }, [mergedRoster]);
  // The docked tree's React-Flow graph ({nodes,edges}) from the SAME manifest the roster is seeded
  // from — one source of truth for topology.
  const networkAslGraph = useMemo(
    () => (isManifest(networkAsl) ? manifestToReactFlow(networkAsl) : { nodes: [], edges: [] }),
    [networkAsl],
  );
  // DIMMED (capped-out) agents for the docked tree: agentIds deeper than the FROZEN release stage
  // cap → "part of the team, disabled this release". Derived from the seeded roster (carries
  // ``level``) so it matches the manifest the tree is drawn from. Empty set when cap=0 (Auto).
  const roomDimmedAgentIds = useMemo(
    () => cappedOutAgentIds(seededRoster, stageDepthCap),
    [seededRoster, stageDepthCap],
  );
  // Which agent the user clicked in the docked tree → highlights the node + loads that agent into
  // the shared container (two-way with the line-item panel's own selection).
  const [selectedTreeAgentId, setSelectedTreeAgentId] = useState(null);

  // FOCUSED RUN (multi-run disambiguation): with several run blocks stacked — each carrying its OWN
  // graph — the user needs a clear visual anchor for "which execution's graph am I looking at". We
  // track the focused runId; ``null`` resolves to the newest run at render (default = latest/live).
  // Clicking a run block focuses it. Only applied when >1 block is on screen (a lone run needs none).
  const [focusedRunId, setFocusedRunId] = useState(null);

  // MULTI-BUILDER controls (Step 4): derive the "N awaiting" notice, the selected wait's band, and
  // the per-row Stop targets from the reducer's run-level roster + pending-waits list. Replaces the
  // single-``activeBuilderCliWait`` path for the hierarchy surface (that path still drives the flat
  // TurnGroup history + the Dynamo-hydrated reconnect band below).
  const builderControls = useBuilderRoomControls({
    pendingBuilderWaits: roomPendingBuilderWaits,
    agentRoster,
    networkId,
    poolId,
    canRespond: canRespondToBuilderApproval(poolRoles, workspaceRole),
  });

  // Per-row builder control (Stop-per-row / Respond) for the LIVE run's tree — shared by the docked
  // panel and the live RunBlock. Only the live run gets these (historical runs are read-only).
  // Respond to an awaiting builder = ONE action → (a) load that builder's stream into the shared
  // window AND (b) open its interaction band above the prompt. The Respond button stops row-click
  // propagation, so we drive the shared selection here too — otherwise the band would open without
  // the stream showing.
  const respondToBuilder = useCallback((agentId) => {
    setSelectedTreeAgentId(agentId);        // shared window → this builder's stream
    builderControls.respondToAgent(agentId); // band above the prompt → this builder's questions
  }, [builderControls]);

  const renderBuilderRowAction = useCallback((row) => (
    <AgentRowBuilderAction
      row={row}
      runningRunId={builderControls.runningBuilderRunIds[row.agentId] || null}
      isAwaiting={row.status === 'awaiting'}
      isSelected={!!builderControls.selectedWait && builderControls.selectedWait.agentId === row.agentId}
      stopping={builderControls.stoppingSet.has(builderControls.runningBuilderRunIds[row.agentId])}
      canRespond={builderControls.canRespond}
      onStop={builderControls.stopBuilder}
      onRespond={respondToBuilder}
    />
  ), [builderControls, respondToBuilder]);

  // ── Pre-flight MCP gate ────────────────────────────────────────────────
  // An agent in this room may enable an *authenticated* MCP (e.g. GitHub).
  // The builder wires MCP servers BEFORE claude-code starts, so a missing
  // credential can't be recovered mid-run — it would silently drop the tool.
  // We block Send up front and tell the user to configure it in Connections.
  // `needsConfig` (computed server-side: enabled ∩ authRequired ∩ unconfigured)
  // is the authority; the room just renders the reason.
  const [mcpNeedsConfig, setMcpNeedsConfig] = useState([]);
  const [mcpGateBump, setMcpGateBump] = useState(0);
  const refreshMcpGate = useCallback(() => setMcpGateBump((n) => n + 1), []);
  useEffect(() => {
    let cancelled = false;
    if (!networkId || networkId === 'new' || !poolId) {
      setMcpNeedsConfig([]);
      return undefined;
    }
    (async () => {
      try {
        const data = await getMcpCredentialStatus(networkId, poolId);
        if (cancelled) return;
        const need = Array.isArray(data?.needsConfig) ? data.needsConfig : [];
        setMcpNeedsConfig(need);
      } catch {
        // Best-effort: a status read failure leaves the gate open (the
        // builder fails safe by skipping uncredentialed MCPs anyway).
        if (!cancelled) setMcpNeedsConfig([]);
      }
    })();
    return () => { cancelled = true; };
    // Re-check when the room/run settles so configuring a credential
    // mid-session clears the gate, and on explicit bumps from Connections.
  }, [networkId, poolId, uiState, mcpGateBump]);
  const mcpGateReason = useMemo(() => {
    if (!mcpNeedsConfig.length) return '';
    // needsConfig is now per-(agent, mcp): { agentId, agentName, id, name }.
    // Summarize by connection name, noting the agent when it's a single one.
    const names = Array.from(
      new Set(mcpNeedsConfig.map((m) => m.name || m.id).filter(Boolean)),
    );
    const agents = Array.from(
      new Set(mcpNeedsConfig.map((m) => m.agentName || m.agentId).filter(Boolean)),
    );
    const who = agents.length === 1 ? ` for ${agents[0]}` : '';
    if (names.length === 1) return `Configure ${names[0]}${who} in Connections to start a run.`;
    return `Configure ${names.join(', ')} in Connections to start a run.`;
  }, [mcpNeedsConfig]);

  // GROUP all turns into RUNS (one collapsible block per run). A run = all turns sharing a runId;
  // turns with no runId (legacy/non-generative) fall under a synthetic key so they still render.
  // Runs ordered NEWEST-FIRST (latest run on top); turn order within a run preserved. Built from ALL
  // turnGroups — the cap below is applied on RUNS, not turns (a generative run has many turns; the
  // old per-turn cap could truncate the middle of one run and mis-count "older runs").
  const allRunBlocks = useMemo(() => {
    const order = [];
    const byRun = new Map();
    for (const t of turnGroups) {
      // Group by the run id. Prefer ``turn.runId`` (stamped when the run's top-level
      // EXECUTION_STARTED was seen), but FALL BACK to ``executionId``/``correlationId`` — for a
      // HISTORICAL run whose EXECUTION_STARTED fell outside the loaded window, ``runId`` is unset,
      // yet its agent events (AGENT_FINAL_RESPONSE/LOG) carry the top-level run id as
      // executionId/correlationId. Without this fallback those turns scatter into per-agent
      // singletons (the "3 individual GOOG blocks" bug). Only a truly id-less turn is a singleton.
      const rid = t.runId || t.executionId || t.correlationId || `__turn__${t.id}`;
      if (!byRun.has(rid)) { byRun.set(rid, []); order.push(rid); }
      byRun.get(rid).push(t);
    }
    // CHAT ORDER: turns arrive chronologically, so ``order`` is already OLDEST-first. Keep it —
    // oldest run on top, newest run at the bottom (like a chat log). Run 1 = oldest.
    return order.map((rid, i) => ({
      runId: rid,
      turns: byRun.get(rid),
      index: i + 1,                                 // Run 1 = oldest (top), highest = newest (bottom)
      isLiveRun: rid === currentRunId,
      releaseVersion: (runReleaseVersions && runReleaseVersions[rid]) || null,
      liveRoster: (agentRostersByRun && agentRostersByRun[rid]) || (rid === currentRunId ? mergedRoster : []),
    }));
  }, [turnGroups, currentRunId, runReleaseVersions, agentRostersByRun, mergedRoster]);

  // SINGLE-RUN VIEW (like the empty room): show ONLY the current/newest run, streaming at the top.
  // Every prior run is folded AWAY entirely (not rendered) — a new prompt is treated exactly like
  // a fresh room. Older runs reveal only when the user asks, via the "Load previous" button at the
  // top (``showPrevious``). Each new prompt RESETS this to the single-run view (see the effect that
  // watches ``currentRunId`` below), so sending always snaps back to "just the current one on top".
  const [showPrevious, setShowPrevious] = useState(false);
  // Newest run = last in the oldest-first ``allRunBlocks``. Collapse to it unless the user expanded.
  const runBlocks = useMemo(() => {
    if (showPrevious || allRunBlocks.length <= 1) return allRunBlocks;
    return allRunBlocks.slice(-1);                 // only the newest/current run
  }, [showPrevious, allRunBlocks]);
  // A NEW RUN (currentRunId changed) → snap back to the single-run view, folding prior runs away.
  useEffect(() => {
    setShowPrevious(false);
  }, [currentRunId]);
  // Are there older runs hidden behind the fold? (drives the top "Load previous" button)
  const hasFoldedRuns = !showPrevious && allRunBlocks.length > 1;

  // Focus only matters when MORE THAN ONE run is on screen (a lone run is unambiguous). The
  // effective focus = the user's pick if it's still present, else the NEWEST run (last, since
  // ``runBlocks`` is oldest-first) — so on load the latest execution's graph is the highlighted one.
  const focusManaged = runBlocks.length > 1;
  const effectiveFocusedRunId = useMemo(() => {
    if (!focusManaged) return null;
    if (focusedRunId && runBlocks.some((rb) => rb.runId === focusedRunId)) return focusedRunId;
    return runBlocks[runBlocks.length - 1].runId;   // newest
  }, [focusManaged, focusedRunId, runBlocks]);

  // Render the CURRENT run's tree in the DOCKED panel when it has a roster + is the visible live run.
  const showRunHierarchy = Array.isArray(mergedRoster) && mergedRoster.length > 0
    && !!currentRunId
    && runBlocks.some((rb) => rb.runId === currentRunId);

  // Clear "Processing…" indicator once stream events start arriving
  useEffect(() => {
    if (sending && turnGroups.length > 0) {
      const latest = turnGroups[turnGroups.length - 1];
      if (latest.status === 'live') setSending(false);
    }
  }, [turnGroups, sending]);

  const resolvedBuilderCliWait = useMemo(() => {
    if (activeBuilderCliWait) return activeBuilderCliWait;
    const h = hydratedBuilderCliWait;
    if (
      h?.runId
      && typeof h.syntheticEventId === 'string'
      && h.syntheticEventId.length > 0
    ) {
      return {
        eventId: h.syntheticEventId,
        runId: h.runId,
        prompt: typeof h.prompt === 'string' ? h.prompt : '',
        interaction: h.interaction && typeof h.interaction === 'object' ? h.interaction : null,
      };
    }
    return null;
  }, [activeBuilderCliWait, hydratedBuilderCliWait]);

  // STOP SWITCH — BUILDER-ONLY. Cancel targets the builder's CodeBuild run, so
  // the band must appear ONLY while the BUILDER (claude-code) is running — NOT
  // during TeamLead/Test/other worker agents (generic uiState===RUNNING is the
  // whole network and is wrong here). Builder-specific signals:
  //   1. a live builder CLI wait (interactive approval) → definitely the builder
  //   2. the latest turn has BUILDER_PHASE events and the last one is NOT a
  //      terminal phase (COMPLETED) → builder still running
  const activeBuilderRunId = useMemo(() => {
    if (resolvedBuilderCliWait?.runId) return resolvedBuilderCliWait.runId;
    const latestTurn = Array.isArray(turnGroups) && turnGroups.length > 0
      ? turnGroups[turnGroups.length - 1] : null;
    const phases = latestTurn && Array.isArray(latestTurn.builderPhases)
      ? latestTurn.builderPhases : [];
    // Bind Stop to the explicit claude-code lifecycle the runner emits:
    // CLAUDE_CODE/started (eager, the instant the SDK launches) → COMPLETED at
    // end. Builder is "running" iff we've seen CLAUDE_CODE and NOT a terminal
    // COMPLETED after it. (Provisioning/clone phases alone don't show Stop —
    // claude-code isn't up yet; other agents never emit builder phases at all.)
    let claudeRunning = false;
    for (const ph of phases) {
      const phase = String(ph.phase || '').toUpperCase();
      const status = String(ph.status || '').toLowerCase();
      if (phase === 'CLAUDE_CODE') claudeRunning = true;
      if (phase === 'COMPLETED' || status === 'completed'
          || status === 'failed' || status === 'skipped') {
        claudeRunning = false;  // terminal — claude-code ended
      }
    }
    if (!claudeRunning) return null;
    // PREFER the builder's OWN runId (captured from BUILDER_PHASE payload.runId
    // == the runner's execution-name). This is the id the builder keys its
    // cancel row + kickoff on; Stop MUST send it. The turn/last-execution
    // ``executionId`` is a fallback only — it can be a runtime UUID that does
    // NOT match the builder's key, which silently no-ops the Stop (the poller
    // watches RUN#<execution-name> while the cancel API would write RUN#<uuid>).
    const rid = (typeof latestTurn.builderRunId === 'string' && latestTurn.builderRunId.trim())
      || (typeof latestTurn.executionId === 'string' && latestTurn.executionId.trim())
      || (typeof lastExecution?.executionId === 'string' && lastExecution.executionId.trim())
      || '';
    // [stop-diag] When claude-code is running, log the resolved target so we can
    // see in the console whether Stop has a runId to act on (and that the
    // CLAUDE_CODE phase actually arrived).
    if (rid) console.debug('[stop-diag] activeBuilderRunId =', rid);
    else console.warn('[stop-diag] claude running but NO builderRunId/executionId on turn — Stop would no-op');
    return rid || null;
  }, [resolvedBuilderCliWait, turnGroups, lastExecution?.executionId]);

  const [stopping, setStopping] = useState(false);
  const handleStopRun = useCallback(async () => {
    // [stop-diag] Trace every Stop click so we can see in the console whether
    // the handler fired, what runId it targeted, and the API outcome.
    console.info('[stop-diag] Stop clicked', {
      activeBuilderRunId, stopping, networkId, poolId,
    });
    if (!activeBuilderRunId || stopping) {
      console.warn('[stop-diag] Stop NO-OP — no activeBuilderRunId or already stopping', {
        activeBuilderRunId, stopping,
      });
      return;
    }
    setStopping(true);
    try {
      const resp = await cancelUniversalBuilderRun(networkId, { runId: activeBuilderRunId, poolId });
      console.info('[stop-diag] cancel API returned', resp);
    } catch (e) {
      // Non-fatal: surface but don't block — the CodeBuild hard-fallback may
      // still have fired server-side.
      console.error('[stop-diag] Stop run failed:', e);
    } finally {
      setStopping(false);
    }
  }, [activeBuilderRunId, stopping, networkId, poolId]);

  const builderCliPromptOpen = !!(
    resolvedBuilderCliWait
    && networkId
    && !handledBuilderWaitSet.has(resolvedBuilderCliWait.eventId)
  );

  // ── Composer builder band + Stop, hierarchy-aware ──────────────────────────
  // When the current run renders as a hierarchy tree, the builder controls come from the
  // multi-wait model: the interaction band shows the wait the user SELECTED on a roster row (one at
  // a time), and Stop is per-row inside the tree — NOT above the prompt. Otherwise (flat/legacy or
  // reconnect-hydrated), keep the single-wait path (``resolvedBuilderCliWait`` + above-prompt Stop).
  const selectedTreeWait = builderControls.selectedWait;
  const composerBuilderCliWait = showRunHierarchy
    ? (selectedTreeWait
      ? {
        eventId: selectedTreeWait.waitEventId,
        runId: selectedTreeWait.runId,
        prompt: selectedTreeWait.prompt || '',
        interaction: selectedTreeWait.interaction || null,
      }
      : null)
    : (builderCliPromptOpen ? resolvedBuilderCliWait : null);
  const composerOnBuilderCliHandled = showRunHierarchy
    ? builderControls.onSelectedWaitHandled
    : () => (resolvedBuilderCliWait ? markBuilderWaitHandled(resolvedBuilderCliWait.eventId) : undefined);
  // Above-prompt Stop only in the flat path; the tree carries per-row Stop.
  const composerActiveBuilderRunId = showRunHierarchy ? null : activeBuilderRunId;

  useEffect(() => {
    if (!networkId || activeBuilderCliWait) {
      setHydratedBuilderCliWait(null);
      return undefined;
    }

    // Only hydrate from Dynamo on initial mount / reconnect — not on a poll loop.
    // The live path is fully event-driven via AppSync BUILDER_INPUT_WAIT.
    // This one-shot check covers the case where the page refreshed while an
    // approval prompt was active and the AppSync event was missed.
    const ls = typeof lastExecution?.status === 'string' ? lastExecution.status.toUpperCase() : '';
    const us = String(uiState || '').toUpperCase();
    const execLooksLive = ls === 'RUNNING' || us === 'RUNNING';
    if (!execLooksLive) {
      setHydratedBuilderCliWait(null);
      return undefined;
    }

    // Only check if we have builder terminal lines in the current turn —
    // this means a builder is actually running (not just a Think agent).
    const latestTurn = Array.isArray(turnGroups) && turnGroups.length > 0
      ? turnGroups[turnGroups.length - 1]
      : null;
    const hasBuilderActivity = latestTurn && (
      (Array.isArray(latestTurn.builderTerminalLines) && latestTurn.builderTerminalLines.length > 0)
      || (Array.isArray(latestTurn.builderPhases) && latestTurn.builderPhases.length > 0)
    );
    if (!hasBuilderActivity) {
      setHydratedBuilderCliWait(null);
      return undefined;
    }

    let runIdRaw = typeof lastExecution?.executionId === 'string' ? lastExecution.executionId.trim() : '';
    if (!runIdRaw && latestTurn) {
      runIdRaw = typeof latestTurn.executionId === 'string' ? latestTurn.executionId.trim() : '';
    }
    if (!runIdRaw) {
      setHydratedBuilderCliWait(null);
      return undefined;
    }

    let cancelled = false;

    async function hydrateOnce() {
      try {
        const slot = await fetchBuilderCliSlot(networkId, runIdRaw, poolId);
        if (cancelled) return;
        const awaiting =
          Boolean(slot?.inputWaitActive) && slot?.hasLine !== true;
        if (awaiting) {
          const wo = Number(slot?.waitOpenedAt ?? 0) || 0;
          const ps = typeof slot?.promptSnippet === 'string' ? slot.promptSnippet : '';
          const ix = slot?.interaction && typeof slot.interaction === 'object' ? slot.interaction : null;
          setHydratedBuilderCliWait({
            syntheticEventId: `hydrated-builder-cli:${runIdRaw}:${wo}`,
            runId: runIdRaw,
            prompt: ps,
            interaction: ix,
          });
        } else {
          setHydratedBuilderCliWait(null);
        }
      } catch {
        if (!cancelled) setHydratedBuilderCliWait(null);
      }
    }

    hydrateOnce();
    return () => { cancelled = true; };
  }, [activeBuilderCliWait, networkId, poolId, lastExecution?.executionId, lastExecution?.status, turnGroups.length, uiState]);

  /** Suppress the reducer's pendingApproval until the matching
   *  APPROVAL_RESOLVED has propagated; otherwise the UI flickers back to
   *  AWAITING_APPROVAL between click and echo. */
  const pendingApproval = useMemo(() => {
    const p = room.pendingApproval;
    if (!p) return null;
    if (resolvedTaskIds.includes(p.taskId)) return null;
    return p;
  }, [room.pendingApproval, resolvedTaskIds]);

  /** Drop optimistic bubbles whose correlationId now appears in the
   *  reduced feed (the backend echoed our prompt). */
  useEffect(() => {
    if (optimisticBubbles.length === 0) return;
    const confirmedCorrelations = new Set(
      room.feed
        .filter((e) => e.kind === 'user' && e.eventId && e.correlationId)
        .map((e) => e.correlationId),
    );
    if (confirmedCorrelations.size === 0) return;
    setOptimisticBubbles((prev) => prev.filter((b) => !confirmedCorrelations.has(b.correlationId)));
  }, [room.feed, optimisticBubbles.length]);

  /** Once APPROVAL_RESOLVED has been processed by the reducer the row
   *  drops from `room.pendingApproval` — clear our local override so a
   *  subsequent fresh approval can show. */
  useEffect(() => {
    if (resolvedTaskIds.length === 0) return;
    if (room.pendingApproval) return;
    setResolvedTaskIds([]);
  }, [room.pendingApproval, resolvedTaskIds.length]);

  /** Pin the bottom: as content arrives (new turn, builder terminal lines,
   *  agent text, thinking lines) keep the latest message just above the
   *  composer so nothing gets hidden behind it. The live-content signal
   *  bumps whenever the active turn grows, not just on new-turn opens. */
  const liveTurn = turnGroups.length > 0 ? turnGroups[turnGroups.length - 1] : null;
  // FINAL SUMMARY autoscroll: the synthesis streams token-by-token into the Summary roster row's
  // ``streamText`` (SUMMARY_DELTA), NOT into agentEntries — so the counts below never move while
  // the answer composes, and the growing text slid UNDER the composer with no scroll. Fold the
  // summary's LENGTH into the signal so each streamed chunk re-pins the bottom (the block sits just
  // above the prompt box as it writes). Length (not identity) is the signal: it bumps per chunk.
  const _summaryStreamLen = (() => {
    const rows = Array.isArray(agentRoster) ? agentRoster : [];
    const s = rows.find((r) => r && r.agentId === 'Summary');
    return (s && typeof s.streamText === 'string') ? s.streamText.length : 0;
  })();
  const liveContentSignal =
    (liveTurn?.builderTerminalLines?.length || 0)
    + (liveTurn?.agentEntries?.length || 0) * 100
    + (liveTurn?.thinking?.length || 0) * 13
    + _summaryStreamLen;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turnGroups.length, liveContentSignal, pendingApproval, uiState]);

  // Approval claim derivations -----------------------------------------------
  const pendingTaskId = pendingApproval?.taskId || '';
  const currentLease = pendingTaskId ? getApprovalLease(pendingTaskId) : null;
  const iHoldClaim = pendingTaskId ? isSelfClaimer(pendingTaskId) : false;
  const peerHoldsClaim = !!(
    currentLease && currentLease.live && currentLease.claimerSub && !iHoldClaim
  );
  const peerLabel = peerHoldsClaim
    ? currentLease.claimerEmail || currentLease.claimerSub?.slice(0, 8) || 'a peer'
    : '';

  // Composer callbacks -------------------------------------------------------
  const handleSendPrompt = useCallback(
    async (text, { stagingId, attachments, correlationId }) => {
      if (!text) return;
      setSending(true);
      // Optimistic bubble — appears in the feed instantly so the user sees
      // their turn. Reconciled by `correlationId` once the backend lands a
      // ``publish_user_prompt`` echo (still pending in some paths; the
      // reducer carries the bubble forward gracefully until then).
      const bubble = freshOptimisticBubble({
        text,
        actorName: user?.email || user?.name || 'You',
        correlationId,
      });
      setOptimisticBubbles((prev) => [...prev, bubble]);

      // Mirror the Network Workbench ``Run`` tab shape: ``input.tenantId`` is
      // the knowledge / storage tenant. For agentic rooms that is the pool id
      // (e.g. ``milo``) — the same value the server injects on
      // ``/pools/{poolId}/execute``; including it here keeps DevTools and
      // tracing aligned with a direct ``POST /networks/{id}/execute`` run
      // where you typed a tenant in the workbench.
      // ``source: 'ui'`` tags the run's Trigger provenance as UI (vs API for
      // direct/SDK callers, JOB for the scheduler). Attribution for the
      // Executions tab — not a security boundary.
      const body = { input: { prompt: text, tenantId: poolId }, logLevel: 'INFO', source: 'ui' };
      if (Array.isArray(attachments) && attachments.length > 0) {
        body.attachmentStagingId = stagingId;
        body.attachmentInput = buildAttachmentInputBody(attachments);
      }
      try {
        await poolExecute(networkId, poolId, body);
        // Don't clear sending here — let the first stream event clear it.
        // Safety timeout: clear after 30s if no events arrive.
        setTimeout(() => setSending(false), 30000);
      } catch (e) {
        console.error(e);
        setSending(false);
        setOptimisticBubbles((prev) => prev.filter((entry) => entry.id !== bubble.id));
        if (e instanceof IncludedTokensExceededError) {
          pushLocalSystemEvent(
            'Run blocked: this organization has used all included tokens for this billing period. An admin can upgrade the plan to keep running.',
          );
        } else {
          pushLocalSystemEvent(e.message || 'Execution failed');
        }
      }
    },
    [networkId, poolId, user?.email, user?.name, pushLocalSystemEvent],
  );

  const handleClaim = useCallback(async () => {
    if (!pendingTaskId) return;
    setApprovalBusy(true);
    try {
      await claimApprovalLease(pendingTaskId);
    } catch (e) {
      pushLocalSystemEvent(e?.data?.message || e.message || 'Could not claim approval');
    } finally {
      setApprovalBusy(false);
    }
  }, [pendingTaskId, claimApprovalLease, pushLocalSystemEvent]);

  const handleEscalate = useCallback(async () => {
    if (!pendingTaskId) return;
    setApprovalBusy(true);
    try {
      await escalateApprovalLease(pendingTaskId, {
        reason: 'Escalated from the room',
      });
      pushLocalSystemEvent('Approval escalated. Workspace admins will see it in Overwatch.');
    } catch (e) {
      pushLocalSystemEvent(e.message || 'Escalation failed');
    } finally {
      setApprovalBusy(false);
    }
  }, [pendingTaskId, escalateApprovalLease, pushLocalSystemEvent]);

  const handleDelegate = useCallback(() => {
    if (!pendingTaskId || !iHoldClaim) return;
    setDelegateDialogOpen(true);
  }, [pendingTaskId, iHoldClaim]);

  const handleDelegateSubmit = useCallback(
    async ({ toSub, toEmail, reason }) => {
      if (!pendingTaskId || !iHoldClaim) {
        throw new Error(
          iHoldClaim
            ? 'No pending approval to delegate.'
            : 'Claim has been reassigned — reclaim first.',
        );
      }
      setApprovalBusy(true);
      try {
        await delegateApprovalLease(pendingTaskId, { toSub, toEmail, reason });
        pushLocalSystemEvent(`Delegated to ${toEmail || toSub}.`);
      } finally {
        setApprovalBusy(false);
      }
    },
    [pendingTaskId, iHoldClaim, delegateApprovalLease, pushLocalSystemEvent],
  );

  const handleApprovalDecision = useCallback(
    async (decision) => {
      if (!pendingApproval?.taskToken) return;
      if (!iHoldClaim) {
        pushLocalSystemEvent('Claim the approval before resolving it.');
        return;
      }
      const taskId = pendingApproval.taskId;
      setApprovalBusy(true);
      try {
        await poolApproval(networkId, poolId, {
          taskToken: pendingApproval.taskToken,
          taskId: taskId || undefined,
          decision,
        });
        if (taskId) setResolvedTaskIds((prev) => (prev.includes(taskId) ? prev : [...prev, taskId]));
      } catch (e) {
        pushLocalSystemEvent(e.message || 'Approval failed');
      } finally {
        setApprovalBusy(false);
      }
    },
    [networkId, poolId, pendingApproval, iHoldClaim, pushLocalSystemEvent],
  );

  const handleSendModify = useCallback(
    async (text /* , { stagingId, attachments } — see Composer comment */) => {
      if (!pendingApproval?.taskToken || !text) return;
      if (!iHoldClaim) {
        pushLocalSystemEvent('Claim the approval before sending feedback.');
        return;
      }
      const taskId = pendingApproval.taskId;
      setApprovalBusy(true);
      try {
        await poolApproval(networkId, poolId, {
          taskToken: pendingApproval.taskToken,
          taskId: taskId || undefined,
          decision: 'MODIFY',
          feedback: text,
        });
        if (taskId) setResolvedTaskIds((prev) => (prev.includes(taskId) ? prev : [...prev, taskId]));
      } catch (e) {
        pushLocalSystemEvent(e.message || 'Modify failed');
      } finally {
        setApprovalBusy(false);
      }
    },
    [networkId, poolId, pendingApproval, iHoldClaim, pushLocalSystemEvent],
  );

  const canManagePoolSettings = useMemo(
    () =>
      (poolRole || '').toUpperCase() === 'CREATOR'
      || ['OWNER', 'ADMIN'].includes(String(workspaceRole || '').toUpperCase()),
    [poolRole, workspaceRole],
  );

  // Connector management is wider than roster management: pool BUILDER also
  // qualifies, since they're the role that responds to Universal Builder
  // approvals at runtime and may need to swap credentials mid-flight.
  const canManageConnectors = useMemo(
    () => canManageRoomConnectors(poolRoles, workspaceRole),
    [poolRoles, workspaceRole],
  );

  const canExecute = useMemo(() => {
    // Two-axis gate (Phase B): identity × trigger mode.
    //
    // 1. Identity (Phase A): only the pool CREATOR can start a run from
    //    the prompt box. Pool SUPERVISORs see the box at IDLE in
    //    disabled / read-only mode; they only get an editable prompt
    //    during an AWAITING_APPROVAL + Modify turn (gated independently
    //    in `composerState.js`).
    // 2. Trigger mode (Phase B): pools released as HEADLESS reject UI-
    //    driven starts entirely — the prompt box is read-only for every
    //    member at IDLE, regardless of role. Approvals still work
    //    (mirrors the backend's intent: "humans approve, robots start").
    //    HYBRID and MANUAL both allow CREATOR-driven manual starts.
    //
    // Workspace OWNER / ADMIN bypass the pool-role check via Overwatch
    // for break-glass reasons, but the trigger-mode gate still applies
    // to them — admins flipping a pool to HEADLESS are explicitly opting
    // every human (including themselves) out of the manual path.
    //
    // The UI gate is purely for clarity — the backend's
    // `pool_execute_handler` is the security boundary.
    if (triggerMode === TRIGGER_HEADLESS) return false;
    const wr = String(workspaceRole || '').toUpperCase();
    if (wr === 'VIEWER') return false;
    if (wr === 'OWNER' || wr === 'ADMIN') return true;
    const pr = String(poolRole || '').toUpperCase();
    return pr === 'CREATOR';
  }, [triggerMode, workspaceRole, poolRole]);

  // Lifecycle freeze + banner (M5).
  // ``isAdmin`` only matters for the "should this row be hidden from
  // the sidebar?" decision — the chat surface here is post-routing,
  // so we always render the banner / freeze for the explicit status.
  const lifecycleUi = useMemo(
    () => projectRoomLifecycleUiState(
      { lifecycleStatus, inactivityWarning },
      { isAdmin: ['OWNER', 'ADMIN'].includes(String(workspaceRole || '').toUpperCase()) },
    ),
    [lifecycleStatus, inactivityWarning, workspaceRole],
  );
  const lifecycleFrozen = lifecycleUi.frozen;

  const roomTitle = displayName || `${networkId} · ${poolId}`;

  // ``networkAsl`` (topology) is fetched above (near the seeded roster) and reused here for
  // ``networkAsl`` (topology) is fetched above (near the seeded roster) and feeds the docked
  // live agent-tree; the tree's per-node status comes from ``treeStatusByAgentId`` (merged roster).

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white">
      {/* (Global search lives in WorkbenchShell's top bar — not duplicated here.
          Room uiState + AppSync-connection show as chips beside the room title.) */}
      <div className="relative shrink-0 border-b border-gray-200 bg-white px-3 py-2 sm:px-4">
        <div className="flex min-h-[36px] items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {/* Room's universal avatar — a circle in its NETWORK's hue family
                (same identity as the dashboard room row), so a room looks the
                same everywhere and visibly belongs to its network. */}
            {networkId && poolId ? (
              <RoomAvatar networkId={networkId} poolId={poolId} name={displayName} size={24} />
            ) : null}
            <h1 className="truncate text-base font-semibold leading-tight text-gray-900" title={roomTitle}>
              {roomTitle}
            </h1>
            {/* Version label — sits next to the room name so users always
                see which slice of the network this room is talking to. */}
            {versionLabel ? (
              <span
                className={`shrink-0 rounded-full px-1.5 py-px text-[10px] font-medium ring-1 ${
                  connected
                    // Green when subscribed to AppSync — the version chip IS the
                    // room's live-connection indicator (no separate dot here).
                    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                    : 'bg-blue-50 text-blue-700 ring-blue-200'
                }`}
                title={
                  connected
                    ? `Release version this room is pinned to · AppSync subscribed`
                    : `Release version this room is pinned to · AppSync connecting…`
                }
              >
                v{versionLabel}
              </span>
            ) : null}
            {/* Room UI-state chip — sits right of the Version chip. Friendly label, never the
                raw engine enum (e.g. "AWAITING_INITIAL_INPUT" → "Ready"). */}
            {uiState ? (
              <span
                className="shrink-0 rounded-full bg-slate-200/80 px-1.5 py-px text-[10px] font-medium text-slate-800"
                title={`Room state: ${friendlyUiState(uiState)}`}
              >
                {friendlyUiState(uiState)}
              </span>
            ) : null}
            {/* Economy/Test mode chip — shown only when this room runs cheap (the frozen
                Shared setting). Amber so it reads as an active cost-saving mode, not an error. */}
            {economyMode ? (
              <span
                className="shrink-0 rounded-full bg-amber-100 px-1.5 py-px text-[10px] font-medium text-amber-800 ring-1 ring-amber-200"
                title="Economy mode: this room runs on the fastest, lowest-cost model with concise answers. Turn off in the workbench Shared settings for full-quality runs."
                data-testid="room-economy-chip"
              >
                Economy
              </span>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* Stream alignment toggle — left utilizes full chat-panel width
                (Slack default), centered keeps the legacy 3xl-cap layout.
                Personal preference, persisted across rooms. */}
            <div
              role="radiogroup"
              aria-label="Stream alignment"
              className="inline-flex items-center rounded-md border border-gray-300 bg-white p-0.5"
            >
              <button
                type="button"
                role="radio"
                aria-checked={streamAlign === 'left'}
                onClick={() => setStreamAlign('left')}
                className={`inline-flex items-center justify-center rounded px-1.5 py-0.5 transition-colors ${
                  streamAlign === 'left' ? 'bg-gray-100 text-gray-800' : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Full width (stream spans the whole panel)"
              >
                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
                  <path d="M2 4h10M2 8h7M2 12h10" />
                </svg>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={streamAlign === 'center'}
                onClick={() => setStreamAlign('center')}
                className={`inline-flex items-center justify-center rounded px-1.5 py-0.5 transition-colors ${
                  streamAlign === 'center' ? 'bg-gray-100 text-gray-800' : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Focused (centered stream + agent tree)"
              >
                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
                  <path d="M3 4h10M5 8h6M3 12h10" />
                </svg>
              </button>
            </div>
            <button
              type="button"
              onClick={() => setMembersDrawerOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
              title="Members, trigger mode, agent supervisors, and connector overrides"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3.5 w-3.5 opacity-70"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h.01a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51h.01a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.01a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
              </svg>
              Settings
              {typeof memberCount === 'number' ? (
                <span
                  className="rounded-full bg-gray-100 px-1.5 text-[10px] font-semibold text-gray-600"
                  title={`${memberCount} active member${memberCount === 1 ? '' : 's'}`}
                >
                  {memberCount}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              onClick={() => setContextDrawerOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
              title="See the context exactly as the agent receives it (RULES verbatim, REFERENCE retrieved at runtime)"
            >
              View context
            </button>
          </div>
        </div>
        {/* Room budget (USAGE-AND-BUDGET.md §5b) as the header's underline — a
            thin bar on the bottom border, filled to consumed %, transitioning
            green → amber (80%) → red (95%+). Self-gates: nothing when the room
            has no allocation. */}
        <RoomBudgetGauge networkId={networkId} poolId={poolId} variant="border" />
      </div>


      <Tab.Group
        as="div"
        selectedIndex={roomTabIndex}
        onChange={setRoomTabIndex}
        className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      >
        <div className="min-w-0 shrink-0 border-b border-gray-200 bg-white px-3 sm:px-4">
          <Tab.List className="-mb-px flex w-full min-w-0 flex-wrap items-center gap-x-1 overflow-x-auto pb-px sm:gap-x-2">
            <Tab
              className={({ selected: sel }) =>
                `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                  sel
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                }`
              }
            >
              <List className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              Activities
            </Tab>
            <Tab
              className={({ selected: sel }) =>
                `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                  sel
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                }`
              }
            >
              <Database className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              Context
            </Tab>
            <Tab
              className={({ selected: sel }) =>
                `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                  sel
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                }`
              }
            >
              <Plug className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              Connections
            </Tab>
            <Tab
              className={({ selected: sel }) =>
                `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                  sel
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                }`
              }
            >
              <FolderTree className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              Workspace
            </Tab>
            <Tab
              className={({ selected: sel }) =>
                `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                  sel
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                }`
              }
            >
              <History className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              Executions
            </Tab>
            <Tab
              className={({ selected: sel }) =>
                `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                  sel
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                }`
              }
            >
              <CalendarClock className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
              Scheduler
            </Tab>
          </Tab.List>
        </div>

        <Tab.Panels className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            {/* ONE WHITE ROOM SURFACE. This row holds the stream (left) and the docked agent-tree
                panel (right, ``bg-white``). It used to be ``bg-gradient-to-br from-gray-50
                to-blue-50``, which is what made every card in the stream read as a separate window
                floating on tinted paper — a white run block on a gray-blue wash is a visible tile
                no matter how its own borders are styled. Flat white here means the stream, the
                cards and the ReactFlow tree panel are all the same sheet, and the only visual
                separation left is the panels' own hairline borders. */}
            <div className="relative flex min-h-0 min-w-0 flex-1 flex-row bg-white">
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                {lifecycleUi.bannerText ? (
                  <LifecycleBanner
                    text={lifecycleUi.bannerText}
                    tone={lifecycleUi.bannerTone}
                  />
                ) : null}
                <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-12 pt-6">
                  <div className={streamAlign === 'center' ? 'mx-auto max-w-3xl' : 'w-full'}>
                    <div className={`${WORKBENCH_ACTIVITIES_STREAM_OUTER_PADDING} space-y-4`}>
                    {turnGroups.length === 0 && localSystemEvents.length === 0 ? (
                      !historyLoaded ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center py-12">
                          <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-500 shadow-sm">
                            No activity yet. Send a prompt to start a run.
                          </span>
                        </div>
                      )
                    ) : null}
                    {/* LOAD PREVIOUS — the room shows ONLY the current run (like a fresh room); prior
                        runs are folded away. This top button reveals them on demand. Two sources of
                        "more above": (a) runs already loaded in memory but folded (``hasFoldedRuns``)
                        → just expand them (no fetch); (b) older runs still on the channel
                        (``historyHasMore``) → page one in. Show the button if EITHER exists; a click
                        expands the fold first, else pages the channel. */}
                    {(hasFoldedRuns || (historyLoaded && historyHasMore)) ? (
                      <div className="flex justify-center pb-1">
                        <button
                          type="button"
                          onClick={() => {
                            if (hasFoldedRuns) setShowPrevious(true);   // reveal in-memory folded runs
                            else loadMoreHistory();                     // else fetch older from the channel
                          }}
                          disabled={historyPaging}
                          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-600 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 disabled:opacity-60"
                          data-testid="load-previous-run"
                        >
                          {historyPaging ? (
                            <><Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Loading…</>
                          ) : (
                            'Load previous'
                          )}
                        </button>
                      </div>
                    ) : null}
                    {/* PER-RUN BLOCKS — each run is a collapsible block (newest on top). Header
                        click loads that run; each renders its OWN turns + (for historical runs) its
                        own tree on ITS topology. The LIVE run's tree is in the docked right panel. */}
                    {runBlocks.map((rb) => (
                      // Each block in its OWN error boundary — a single bad run (malformed manifest,
                      // etc.) shows an inline error with the real message instead of blanking the
                      // WHOLE stream (which is what "my blocks disappeared" was).
                      <RouteErrorBoundary key={rb.runId} routeName={`run:${rb.index}`}>
                        <RunBlock
                          turns={rb.turns}
                          liveRoster={rb.liveRoster}
                          releaseVersion={rb.releaseVersion}
                          isLiveRun={rb.isLiveRun}
                          networkId={networkId}
                          poolId={poolId}
                          stageDepthCap={stageDepthCap}
                          stageCost={(stageCostByRun && stageCostByRun[rb.runId]) || null}
                          verbose={verboseMode}
                          builderControls={builderControls}
                          handledBuilderWaitSet={handledBuilderWaitSet}
                          resolvedWaitEventIds={resolvedWaitEventIds}
                          renderRowAction={renderBuilderRowAction}
                          // FOCUS: highlight the run whose graph is in view; click a dimmed block to
                          // focus it. Only wired when >1 run is on screen (a lone run needs no anchor).
                          runId={rb.runId}
                          {...(focusManaged
                            ? { focused: rb.runId === effectiveFocusedRunId, onFocus: setFocusedRunId }
                            : {})}
                          // The LIVE run shares its selection with the docked tree (click a tree node
                          // → loads that agent here; switching builders swaps the live stream). Only
                          // the live run — the dock tracks the live run only.
                          {...(rb.isLiveRun
                            ? { selectedAgentId: selectedTreeAgentId, onSelectAgent: setSelectedTreeAgentId }
                            : {})}
                        />
                      </RouteErrorBoundary>
                    ))}
                    {localSystemEvents.length > 0 ? (
                      <div className="space-y-2">
                        {localSystemEvents.map((sys) => (
                          <div
                            key={sys.id}
                            className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900"
                          >
                            <div className="whitespace-pre-wrap">{sys.text}</div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {sending && (
                      <div className="flex items-center gap-2 px-1 py-2">
                        <span className="inline-block h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                        <span className="inline-block h-2 w-2 rounded-full bg-blue-500 animate-pulse [animation-delay:150ms]" />
                        <span className="inline-block h-2 w-2 rounded-full bg-blue-500 animate-pulse [animation-delay:300ms]" />
                        <span className="text-[11px] text-slate-400 ml-1">Processing…</span>
                      </div>
                    )}
                    <div ref={bottomRef} />
                    </div>
                  </div>
                </div>

                <Composer
                  align={streamAlign}
                  verbose={verboseMode}
                  networkId={networkId}
                  poolId={poolId}
                  canExecute={canExecute}
                  uiState={uiState}
                  pendingApproval={pendingApproval}
                  iHoldClaim={iHoldClaim}
                  peerHoldsClaim={peerHoldsClaim}
                  peerLabel={peerLabel}
                  approvalBusy={approvalBusy}
                  sending={sending}
                  frozen={lifecycleFrozen}
                  frozenReason={lifecycleUi.disabledReason}
                  mcpGateReason={mcpGateReason}
                  onSendPrompt={handleSendPrompt}
                  onClaim={handleClaim}
                  onApprove={() => handleApprovalDecision('APPROVE')}
                  onDeny={() => handleApprovalDecision('DENY')}
                  onSendModify={handleSendModify}
                  onDelegate={handleDelegate}
                  onEscalate={handleEscalate}
                  builderCliWait={composerBuilderCliWait}
                  onBuilderCliHandled={composerOnBuilderCliHandled}
                  canRespondToBuilderApproval={canRespondToBuilderApproval(poolRoles, workspaceRole)}
                  activeBuilderRunId={composerActiveBuilderRunId}
                  stoppingRun={stopping}
                  onStopRun={handleStopRun}
                />
              </div>

              {/* Docked agent-tree panel — replaces the old floating MiniFlow overlay. Uses the
                  side-space freed by the centered stream to show the WHOLE topology as a live tree
                  (ManifestTreeFlow, same class as the workbench/agent-config). Nodes color by the
                  AppSync stream in real time (running/awaiting/done/failed/skipped). Clicking a node
                  loads that agent into the shared container. Shown when centered + a topology exists
                  + the user hasn't hidden it. */}
              {/* The panel FRAME shows whenever centered — decoupled from manifest availability so
                  it's never a phantom (blank frame > nothing). The tree CONTENT depends on the
                  manifest: ManifestTreeFlow renders "No diagram available yet" until it loads. */}
              {streamAlign === 'center' ? (
                <div
                  className="relative hidden md:flex shrink-0 flex-col border-l border-slate-200 bg-white min-h-0"
                  style={{ width: `${treePanelWidth}px` }}
                >
                  {/* Drag-to-resize divider on the panel's left edge. */}
                  <div
                    onMouseDown={onTreeDividerDown}
                    className="absolute left-0 top-0 z-10 h-full w-1.5 -translate-x-1/2 cursor-col-resize hover:bg-blue-300/50"
                    title="Drag to resize"
                    role="separator"
                    aria-orientation="vertical"
                  />
                  <div className="relative min-h-0 flex-1">
                    {isManifest(networkAsl) ? (
                      <ManifestTreeFlow
                        data={networkAslGraph}
                        statusByAgentId={treeStatusByAgentId}
                        highlightLogicalId={selectedTreeAgentId}
                        onAgentClick={setSelectedTreeAgentId}
                        dimmedAgentIds={roomDimmedAgentIds}
                        canvasClassName="bg-white"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center px-4 text-center text-[11px] text-slate-400">
                        Agent tree loads when the network topology is available.
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </Tab.Panel>

          <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <AgenticDataPlaneTab
              networkId={networkId}
              poolId={poolId}
              viewerEmail={user?.email}
              poolRole={poolRole}
              workspaceRole={workspaceRole}
              lifecycleFrozen={lifecycleFrozen}
            />
          </Tab.Panel>

          <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white outline-none">
            {activeTenantId ? (
              secretsRegistryV1Enabled() ? (
                <SecretsCatalog
                  tenantId={activeTenantId}
                  catalogNetworkId={networkId}
                  catalogPoolId={poolId}
                  allowCreate={canManagePoolSettings}
                  workspaceRole={workspaceRole}
                  poolRole={poolRole}
                  viewerUserSub={user?.sub}
                  networkOwnerUserId={networkOwnerUserId}
                  autoOpenTool={connectionsAutoOpenTool}
                  onAutoOpenConsumed={() => setConnectionsAutoOpenTool(null)}
                />
              ) : (
                <div className="p-6 text-sm text-gray-600">
                  Connections need the secrets registry in this deployment (
                  <code className="rounded bg-gray-100 px-1 text-xs">VITE_STEPFLOW_SECRETS_REGISTRY_V1</code>
                  ).
                </div>
              )
            ) : (
              <div className="p-6 text-sm text-gray-600">
                Sign in with an organization tenant to view and manage connections.
              </div>
            )}
          </Tab.Panel>

          <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white">
            <AgentWorkspaceBrowser networkId={networkId} poolId={poolId} />
          </Tab.Panel>

          <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <ExecutionsTab
              networkId={networkId}
              poolId={poolId}
              messages={messages}
            />
          </Tab.Panel>

          <Tab.Panel className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <SchedulerTab
              networkId={networkId}
              poolId={poolId}
              canManage={canManagePoolSettings}
            />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      <ContextPreviewPanel
        open={contextDrawerOpen}
        onClose={() => setContextDrawerOpen(false)}
        networkId={networkId}
        poolId={poolId}
        roomLabel={displayName || `${networkId} · ${poolId}`}
      />

      <PoolSettingsDrawer
        open={membersDrawerOpen}
        onClose={() => setMembersDrawerOpen(false)}
        networkId={networkId}
        poolId={poolId}
        poolLabel={displayName || `${networkId} · ${poolId}`}
        tenantId={activeTenantId || ''}
        currentUserSub={user?.sub}
        canManage={canManagePoolSettings}
        canManageConnectors={canManageConnectors}
        channelEvents={messages}
        onRosterChange={handleMemberCountFromRoster}
        triggerMode={triggerMode}
        onTriggerModeChange={handleTriggerModeFromDrawer}
        onNavigateToConnections={navigateRoomToConnections}
      />

      <DelegateMemberDialog
        open={delegateDialogOpen}
        networkId={networkId}
        poolId={poolId}
        currentUserSub={user?.sub}
        onClose={() => setDelegateDialogOpen(false)}
        onConfirm={handleDelegateSubmit}
      />

    </div>
  );
}

const LIFECYCLE_BANNER_TONES = {
  danger: 'border-red-200 bg-red-50 text-red-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  muted: 'border-gray-200 bg-gray-100 text-gray-700',
};

function LifecycleBanner({ text, tone }) {
  const palette = LIFECYCLE_BANNER_TONES[tone] || LIFECYCLE_BANNER_TONES.muted;
  return (
    <div
      role="status"
      aria-live="polite"
      className={`shrink-0 border-b px-4 py-2 text-xs font-medium ${palette}`}
    >
      {text}
    </div>
  );
}

